import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { useSession } from "@/auth/SessionProvider";
import { authSignInUrl } from "@/lib/apiOrigin";
import { apiGet } from "@/lib/apiClient";
import { AtwProductMark } from "@/components/Brand/AtwProductMark";
import { atwMarkIdForAppSlug } from "@/components/Brand/atwProductMarks";
import { CustomAppsCatalogSidebar } from "@/components/Dashboard/CustomAppsCatalogSidebar";
import { DashboardOrgSwitcherSheet } from "@/components/Dashboard/DashboardOrgSwitcherSheet";
import { DashboardUtilityBar } from "@/components/Dashboard/DashboardUtilityBar";
import { ProfileSettingsDialog } from "@/components/Dashboard/ProfileSettingsDialog";
import { TrialExpiredNotice } from "@/components/Dashboard/TrialExpiredNotice";
import { TrialStatusBadge } from "@/components/Dashboard/TrialStatusBadge";
import {
  cnDashboardModuleNavLink,
  DashboardModuleNavIcon,
} from "@/lib/dashboardModuleNav";
import {
  CUSTOM_APPS_CREATE_MODULE_HREF,
  DASHBOARD_APP_BY_SLUG,
  resolveDashboardApp,
  topNavItemActive,
  type DashboardModule,
  type GlobalAppSlug,
} from "@/lib/dashboardAppRegistry";
import {
  getEffectiveDashboardAppSlugs,
  userMayManageCustomApps,
  type OrgProductsResolved,
} from "@/lib/dashboardAppAccess";
import {
  appSettingsBackHref,
  flattenAppSettingsSidebarItems,
  getAppSettingsSidebarItems,
  isAppSettingsSidebarActive,
  parseAppSettingsPath,
  type AppSettingsSidebarItem,
} from "@/lib/appSettingsSidebar";
import { formatSidebarFooterLine } from "@/lib/appVersion";
import { knowledgeModuleNavActive } from "@/lib/knowledgePaths";
import { cn } from "@/lib/utils";

const TOP_NAV_PRIORITY: GlobalAppSlug[] = [
  "home",
  "crm",
  "finance",
  "analytics",
  "marketing",
  "human-resources",
  "projects",
  "knowledge",
  "worksuite",
  "custom-apps",
  "support",
  "platform",
];

const DEFAULT_SIDEBAR_LOGO = "/images/ATWOne%20Logo%20NoERPDark-Photoroom.png";
const SIDEBAR_WIDTH_STORAGE_KEY = "one-sidebar-width";
const SIDEBAR_COLLAPSED_LEGACY_KEY = "one-sidebar-collapsed";
/** 4.5rem at 16px root */
const SIDEBAR_WIDTH_MIN = 72;
/** 20rem */
const SIDEBAR_WIDTH_MAX = 320;
/** 16rem / w-64 */
const SIDEBAR_WIDTH_DEFAULT = 256;

function clampSidebarWidth(width: number): number {
  return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, width));
}

function readSidebarWidthFromStorage(): number {
  try {
    const stored = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (stored != null) {
      const parsed = Number.parseInt(stored, 10);
      if (Number.isFinite(parsed)) return clampSidebarWidth(parsed);
    }
    if (localStorage.getItem(SIDEBAR_COLLAPSED_LEGACY_KEY) === "true") {
      return SIDEBAR_WIDTH_MIN;
    }
  } catch {
    /* ignore storage errors */
  }
  return SIDEBAR_WIDTH_DEFAULT;
}

function isSidebarIconOnly(width: number): boolean {
  return width <= SIDEBAR_WIDTH_MIN;
}

function collapsedNavLinkClass(collapsed: boolean) {
  return collapsed ? "justify-center gap-0 border-l-0 px-2 pl-2" : "";
}

type NavChild = DashboardModule | AppSettingsSidebarItem;

function maxTopNavLinksForViewport(width: number): number {
  if (width < 640) return 2;
  if (width < 768) return 3;
  if (width < 1024) return 4;
  if (width < 1280) return 5;
  return 6;
}

function expandPrefixesForNavChild(child: NavChild): string[] {
  if (child.group && child.groupExpandPrefixes && child.groupExpandPrefixes.length > 0) {
    return child.groupExpandPrefixes;
  }
  return [child.href.split("?")[0]];
}

function isExternalHref(href: string) {
  return href.startsWith("http://") || href.startsWith("https://");
}

function hrefPathOnly(href: string): string {
  const q = href.indexOf("?");
  return q === -1 ? href : href.slice(0, q);
}

type SidebarRailProps = {
  logoFrameClass: string;
  sidebarLogoSrc: string;
  sidebarLogoClass: string;
  themeDefaultSidebarLogo: boolean;
  sidebarTitle: string;
  sidebarItems: NavChild[];
  showCustomAppsCatalog: boolean;
  sidebarFooterHref: string;
  sidebarFooterLabel: string;
  sidebarFooterActive: boolean;
  appSettingsContext: ReturnType<typeof parseAppSettingsPath>;
  pathname: string;
  sidebarGroupOverride: Record<string, boolean>;
  toggleSidebarGroup: (groupHref: string) => void;
  isGroupExpanded: (child: NavChild) => boolean;
  isActiveHref: (href: string) => boolean;
  sidebarFooterLine: string;
  collapsed?: boolean;
  showSidebarCollapseToggle?: boolean;
  onToggleSidebarCollapsed?: () => void;
  onNavigate?: () => void;
  mobileNavClick?: (href: string) => (event: MouseEvent<HTMLAnchorElement>) => void;
};

function SidebarRail({
  logoFrameClass,
  sidebarLogoSrc,
  sidebarLogoClass,
  themeDefaultSidebarLogo,
  sidebarTitle,
  sidebarItems,
  showCustomAppsCatalog,
  sidebarFooterHref,
  sidebarFooterLabel,
  sidebarFooterActive,
  appSettingsContext,
  pathname,
  toggleSidebarGroup,
  isGroupExpanded,
  isActiveHref,
  sidebarFooterLine,
  collapsed = false,
  showSidebarCollapseToggle = false,
  onToggleSidebarCollapsed,
  onNavigate,
  mobileNavClick,
}: SidebarRailProps) {
  const renderNavChildren = (items: NavChild[], depth = 0): ReactNode => (
    <ul
      className={
        depth === 0
          ? "space-y-px"
          : "mt-0.5 ml-2 space-y-px border-l border-slate-600/55 pl-2.5"
      }
    >
      {items.map((child) => {
        const nested = child.children && child.children.length > 0;
        const isGroup = nested && child.group === true;
        const childKey = child.href;

        if (isGroup) {
          const prefixes = expandPrefixesForNavChild(child);
          const sectionActive = prefixes.some(
            (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
          );
          const expanded = isGroupExpanded(child);
          return (
            <li key={childKey}>
              <button
                type="button"
                onClick={() => toggleSidebarGroup(hrefPathOnly(child.href))}
                aria-expanded={expanded}
                title={collapsed ? child.name : undefined}
                className={cn(
                  cnDashboardModuleNavLink(sectionActive),
                  collapsedNavLinkClass(collapsed),
                  "flex w-full border-0 bg-transparent text-left font-[inherit]",
                )}
              >
                {collapsed ? null : (
                  <ChevronRight
                    aria-hidden
                    className={cn(
                      "size-4 shrink-0 transition-transform",
                      expanded ? "rotate-90" : "rotate-0",
                    )}
                  />
                )}
                <DashboardModuleNavIcon href={hrefPathOnly(child.href)} />
                {collapsed ? (
                  <span className="sr-only">{child.name}</span>
                ) : (
                  <span className="min-w-0 truncate">{child.name}</span>
                )}
              </button>
              {expanded ? renderNavChildren(child.children!, depth + 1) : null}
            </li>
          );
        }

        const active = nested
          ? pathname === hrefPathOnly(child.href) ||
            pathname.startsWith(`${hrefPathOnly(child.href)}/`)
          : isActiveHref(child.href);

        const linkClass = cn(cnDashboardModuleNavLink(active), collapsedNavLinkClass(collapsed));
        const inner = (
          <>
            <DashboardModuleNavIcon href={hrefPathOnly(child.href)} />
            {collapsed ? (
              <span className="sr-only">{child.name}</span>
            ) : (
              <span className={depth > 0 ? "min-w-0 truncate text-[12px]" : "min-w-0 truncate"}>
                {child.name}
              </span>
            )}
            {!collapsed && "comingSoon" in child && child.comingSoon ? (
              <span className="ml-auto shrink-0 rounded border border-slate-500/60 px-1 py-px text-[10px] font-medium uppercase tracking-wide text-slate-400">
                Soon
              </span>
            ) : null}
          </>
        );

        return (
          <li key={childKey}>
            {isExternalHref(child.href) ? (
              <a
                href={child.href}
                className={linkClass}
                onClick={onNavigate}
                title={collapsed ? child.name : undefined}
              >
                {inner}
              </a>
            ) : (
              <Link
                to={child.href}
                className={linkClass}
                title={collapsed ? child.name : undefined}
                onClick={(e) => {
                  mobileNavClick?.(child.href)(e);
                  onNavigate?.();
                }}
              >
                {inner}
              </Link>
            )}
            {nested && !isGroup ? renderNavChildren(child.children!, depth + 1) : null}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div
      data-one-rail
      className={cn(
        "flex min-h-full min-w-0 grow flex-col gap-y-4 overflow-y-auto overflow-x-hidden border-r border-slate-800/90 bg-[#111829] py-5 shadow-md ring-1 ring-black/5 dark:border-slate-700/80 dark:ring-white/5",
        collapsed ? "px-2" : "px-3",
      )}
    >
      <div
        className={cn(
          "relative flex min-w-0 shrink-0 items-center justify-center overflow-hidden px-1",
          logoFrameClass,
        )}
      >
        {themeDefaultSidebarLogo ? (
          <img
            src={DEFAULT_SIDEBAR_LOGO}
            alt="ATW One logo"
            className={sidebarLogoClass}
            loading="eager"
            decoding="async"
          />
        ) : (
          <img
            src={sidebarLogoSrc}
            alt="ATW One logo"
            className={sidebarLogoClass}
            loading="eager"
            decoding="async"
          />
        )}
      </div>
      <nav className="flex min-w-0 flex-1 flex-col">
        <ul role="list" className="flex flex-1 flex-col gap-y-6">
          <li>
            {collapsed ? null : (
              <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                {sidebarTitle}
              </p>
            )}
            {renderNavChildren(sidebarItems)}
          </li>

          {showCustomAppsCatalog ? (
            <CustomAppsCatalogSidebar onNavigate={onNavigate} collapsed={collapsed} />
          ) : null}

          <li className="mt-auto border-t border-slate-700/80 pt-3">
            <div
              className={cn(
                "px-1 py-1",
                collapsed ? "flex flex-col items-center gap-1" : "flex items-center gap-1",
              )}
            >
              <Link
                to={sidebarFooterHref}
                className={cn(
                  cnDashboardModuleNavLink(sidebarFooterActive),
                  collapsedNavLinkClass(collapsed),
                  collapsed ? "w-full" : "min-w-0 flex-1",
                )}
                title={
                  collapsed
                    ? appSettingsContext
                      ? sidebarFooterLabel
                      : sidebarFooterLabel
                    : undefined
                }
                onClick={(e) => {
                  mobileNavClick?.(sidebarFooterHref)(e);
                  onNavigate?.();
                }}
              >
                {appSettingsContext ? (
                  <ArrowLeft aria-hidden className="size-4 shrink-0" />
                ) : (
                  <Settings aria-hidden className="size-4 shrink-0" />
                )}
                {collapsed ? (
                  <span className="sr-only">{sidebarFooterLabel}</span>
                ) : (
                  <span className="min-w-0 truncate">{sidebarFooterLabel}</span>
                )}
              </Link>
              {showSidebarCollapseToggle && onToggleSidebarCollapsed ? (
                <button
                  type="button"
                  onClick={onToggleSidebarCollapsed}
                  className="inline-flex shrink-0 items-center justify-center rounded-md p-1.5 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80"
                  aria-label={collapsed ? "Expand sidebar" : "Retract sidebar"}
                >
                  {collapsed ? (
                    <PanelLeftOpen className="size-4 shrink-0" aria-hidden />
                  ) : (
                    <PanelLeftClose className="size-4 shrink-0" aria-hidden />
                  )}
                </button>
              ) : null}
            </div>

            {collapsed ? null : (
              <p
                className="mt-2 truncate px-1 text-center text-[10px] tracking-wide text-slate-400"
                title={sidebarFooterLine}
              >
                {sidebarFooterLine}
              </p>
            )}
          </li>
        </ul>
      </nav>
    </div>
  );
}

export function OneShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useSession();
  const pathname = location.pathname;

  const userRole = session.data?.user?.role ?? "";
  const sessionOrgId = session.data?.user?.orgId?.trim() || null;
  const appEntitlements = session.data?.user?.appEntitlements;
  const canManageCustomAppsFlag = session.data?.user?.canManageCustomApps === true;
  const sessionAccountDomain = session.data?.user?.accountDomain ?? null;

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(readSidebarWidthFromStorage);
  const sidebarWidthRef = useRef(sidebarWidth);
  sidebarWidthRef.current = sidebarWidth;
  const sidebarIconOnly = isSidebarIconOnly(sidebarWidth);
  const [orgSheetOpen, setOrgSheetOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [topNavVisibleCount, setTopNavVisibleCount] = useState(4);
  const [topNavOverflowOpen, setTopNavOverflowOpen] = useState(false);
  const [sidebarGroupOverride, setSidebarGroupOverride] = useState<Record<string, boolean>>({});
  const [tenantLogoUrl, setTenantLogoUrl] = useState<string | null>(null);
  const [orgProducts, setOrgProducts] = useState<OrgProductsResolved | null>(null);
  const [userProfile, setUserProfile] = useState<{
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    profilePhoto?: string | null;
  } | null>(null);

  const topNavOverflowRef = useRef<HTMLDivElement | null>(null);

  const allowedSlugList = useMemo(
    () => getEffectiveDashboardAppSlugs(userRole, appEntitlements, sessionOrgId, orgProducts),
    [userRole, appEntitlements, sessionOrgId, orgProducts],
  );
  const allowedSlugs = useMemo(() => new Set(allowedSlugList), [allowedSlugList]);

  const appSettingsContext = useMemo(() => parseAppSettingsPath(pathname), [pathname]);
  const isSettingsArea =
    pathname === "/settings" ||
    pathname.startsWith("/settings/") ||
    /\/[^/]+\/settings(?:\/|$)/.test(pathname);

  const activeApp = resolveDashboardApp(pathname, allowedSlugs);

  const displayName =
    userProfile?.name ||
    (userProfile?.firstName || userProfile?.lastName
      ? `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim()
      : session.data?.user?.name || session.data?.user?.email?.split("@")[0] || "User");

  const sidebarFooterLine = formatSidebarFooterLine();
  const avatarSrc = userProfile?.profilePhoto || session.data?.user?.image || null;

  const sidebarLogoSrc = tenantLogoUrl || DEFAULT_SIDEBAR_LOGO;
  const sidebarLogoClass = tenantLogoUrl
    ? "h-full w-full object-contain object-center"
    : "h-full w-full scale-[2.2] object-contain object-center";

  const sidebarTitle = appSettingsContext
    ? `${DASHBOARD_APP_BY_SLUG[appSettingsContext.appSlug].label} settings`
    : activeApp.label;

  const sidebarItems: NavChild[] = useMemo(() => {
    if (appSettingsContext) {
      return getAppSettingsSidebarItems(appSettingsContext.appSlug);
    }
    if (activeApp.slug !== "custom-apps") {
      return activeApp.modules;
    }
    return activeApp.modules.filter((m) => {
      if (m.href !== CUSTOM_APPS_CREATE_MODULE_HREF) return true;
      return userMayManageCustomApps(userRole, canManageCustomAppsFlag);
    });
  }, [appSettingsContext, activeApp.modules, activeApp.slug, userRole, canManageCustomAppsFlag]);

  const appSettingsHref =
    activeApp.slug === "home" ? "/settings" : `/${activeApp.slug}/settings?view=hub`;
  const appSettingsLabel = `${activeApp.label} Settings`;
  const sidebarFooterHref = appSettingsContext
    ? appSettingsBackHref(appSettingsContext.appSlug)
    : appSettingsHref;
  const sidebarFooterLabel = appSettingsContext
    ? `Back to ${DASHBOARD_APP_BY_SLUG[appSettingsContext.appSlug].label}`
    : appSettingsLabel;
  const sidebarFooterActive = appSettingsContext ? false : isSettingsArea;
  const showCustomAppsCatalog = !appSettingsContext;

  const topNavApps = useMemo(
    () =>
      TOP_NAV_PRIORITY.filter((slug) => allowedSlugs.has(slug)).map((slug) => ({
        slug,
        label: DASHBOARD_APP_BY_SLUG[slug].label,
        topNavHref: DASHBOARD_APP_BY_SLUG[slug].topNavHref,
      })),
    [allowedSlugs],
  );

  const activeTopNavAppSlug = useMemo(() => {
    const hit = topNavApps.find((app) => topNavItemActive(pathname, app.slug, app.topNavHref));
    return hit?.slug ?? null;
  }, [pathname, topNavApps]);

  const { pinnedTopNavApps, overflowTopNavApps } = useMemo(() => {
    const visibleCount = Math.min(topNavVisibleCount, topNavApps.length);
    const initialPinned = topNavApps.slice(0, visibleCount);
    if (!activeTopNavAppSlug || visibleCount === 0) {
      return {
        pinnedTopNavApps: initialPinned,
        overflowTopNavApps: topNavApps.slice(visibleCount),
      };
    }
    if (
      initialPinned.some((a) => a.slug === activeTopNavAppSlug) ||
      visibleCount === topNavApps.length
    ) {
      return {
        pinnedTopNavApps: initialPinned,
        overflowTopNavApps: topNavApps.slice(visibleCount),
      };
    }
    const activeAppNav = topNavApps.find((a) => a.slug === activeTopNavAppSlug);
    if (!activeAppNav) {
      return {
        pinnedTopNavApps: initialPinned,
        overflowTopNavApps: topNavApps.slice(visibleCount),
      };
    }
    const promoted = [...initialPinned.slice(0, -1), activeAppNav];
    const promotedSet = new Set(promoted.map((a) => a.slug));
    return {
      pinnedTopNavApps: promoted,
      overflowTopNavApps: topNavApps.filter((a) => !promotedSet.has(a.slug)),
    };
  }, [topNavApps, topNavVisibleCount, activeTopNavAppSlug]);

  const overflowActive = overflowTopNavApps.some((app) =>
    topNavItemActive(pathname, app.slug, app.topNavHref),
  );

  const isActiveHref = useCallback(
    (href: string) => {
      if (appSettingsContext) {
        const item = flattenAppSettingsSidebarItems(
          getAppSettingsSidebarItems(appSettingsContext.appSlug),
        ).find((i) => i.href === href);
        if (item) {
          return isAppSettingsSidebarActive(pathname, searchParams, item);
        }
      }
      const pathOnly = hrefPathOnly(href);
      if (pathOnly === "/") return pathname === "/";
      if (pathOnly === "/finance") {
        return pathname === "/finance" || pathname === "/finance/";
      }
      if (pathOnly === "/crm/overview") {
        return pathname === "/crm/overview" || pathname === "/crm/overview/";
      }
      if (
        pathOnly === "/knowledge" ||
        pathOnly === "/knowledge/libraries" ||
        pathOnly === "/knowledge/books" ||
        pathOnly === "/knowledge/articles"
      ) {
        return knowledgeModuleNavActive(pathname, pathOnly);
      }
      return pathname === pathOnly || pathname.startsWith(`${pathOnly}/`);
    },
    [pathname, searchParams, appSettingsContext],
  );

  const isGroupExpanded = useCallback(
    (child: NavChild) => {
      const prefixes = expandPrefixesForNavChild(child);
      const inside = prefixes.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
      );
      const o = sidebarGroupOverride[hrefPathOnly(child.href)];
      if (o === true) return true;
      if (o === false) return false;
      return inside;
    },
    [pathname, sidebarGroupOverride],
  );

  const toggleSidebarGroup = useCallback(
    (groupHref: string) => {
      setSidebarGroupOverride((prev) => {
        const inside = pathname === groupHref || pathname.startsWith(`${groupHref}/`);
        const expanded =
          prev[groupHref] === true ? true : prev[groupHref] === false ? false : inside;
        return { ...prev, [groupHref]: !expanded };
      });
    },
    [pathname],
  );

  useEffect(() => {
    const updateCount = () => setTopNavVisibleCount(maxTopNavLinksForViewport(window.innerWidth));
    updateCount();
    window.addEventListener("resize", updateCount);
    return () => window.removeEventListener("resize", updateCount);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
    } catch {
      /* ignore storage errors */
    }
  }, [sidebarWidth]);

  const toggleSidebarRetractExpand = useCallback(() => {
    setSidebarWidth((width) =>
      isSidebarIconOnly(width) ? SIDEBAR_WIDTH_DEFAULT : SIDEBAR_WIDTH_MIN,
    );
  }, []);

  const handleSidebarResizePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = sidebarWidthRef.current;

    const onPointerMove = (moveEvent: globalThis.PointerEvent) => {
      const delta = moveEvent.clientX - startX;
      setSidebarWidth(clampSidebarWidth(startWidth + delta));
    };

    const onPointerUp = (upEvent: globalThis.PointerEvent) => {
      handle.releasePointerCapture(upEvent.pointerId);
      handle.removeEventListener("pointermove", onPointerMove);
      handle.removeEventListener("pointerup", onPointerUp);
      handle.removeEventListener("pointercancel", onPointerUp);
    };

    handle.setPointerCapture(event.pointerId);
    handle.addEventListener("pointermove", onPointerMove);
    handle.addEventListener("pointerup", onPointerUp);
    handle.addEventListener("pointercancel", onPointerUp);
  }, []);

  useEffect(() => {
    setTopNavOverflowOpen(false);
    setSidebarOpen(false);
  }, [pathname]);

  useEffect(() => {
    setSidebarGroupOverride((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const key of Object.keys(prev)) {
        const inside = pathname === key || pathname.startsWith(`${key}/`);
        if (!inside) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pathname]);

  useEffect(() => {
    const handlePointerDown = (event: globalThis.MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target || topNavOverflowRef.current?.contains(target)) return;
      setTopNavOverflowOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, []);

  useEffect(() => {
    if (session.status !== "authenticated" || !sessionOrgId) {
      setTenantLogoUrl(null);
      return;
    }
    let active = true;
    void (async () => {
      const res = await apiGet<{
        logoUrl?: string | null;
        companyName?: string | null;
      }>(`/api/organization/branding?orgId=${encodeURIComponent(sessionOrgId)}`);
      if (!active) return;
      if (!res.ok || !res.data) {
        setTenantLogoUrl(null);
        return;
      }
      setTenantLogoUrl(res.data.logoUrl || null);
    })();
    return () => {
      active = false;
    };
  }, [session.status, sessionOrgId]);

  useEffect(() => {
    if (session.status !== "authenticated" || !sessionOrgId) {
      setOrgProducts(null);
      return;
    }
    let cancelled = false;
    void apiGet<{ products?: OrgProductsResolved }>(
      `/api/org/${encodeURIComponent(sessionOrgId)}/product-access`,
    ).then((res) => {
      if (!cancelled) {
        setOrgProducts(res.ok ? (res.data?.products ?? null) : null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [session.status, sessionOrgId]);

  useEffect(() => {
    if (session.status !== "authenticated") return;
    const user = session.data?.user;
    if (!user?.email) return;
    const userId = user.id || user.email;
    void (async () => {
      const res = await apiGet<{
        name?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        profilePhoto?: string | null;
      }>(`/api/users/${encodeURIComponent(userId)}`);
      if (res.ok && res.data) {
        setUserProfile({
          name: res.data.name,
          firstName: res.data.firstName,
          lastName: res.data.lastName,
          profilePhoto: res.data.profilePhoto,
        });
      }
    })();
  }, [session.status, session.data?.user]);

  const handleMobileSidebarNavClick = useCallback(
    (href: string) => (event: MouseEvent<HTMLAnchorElement>) => {
      if (!sidebarOpen) return;
      event.preventDefault();
      setSidebarOpen(false);
      window.setTimeout(() => {
        navigate(href);
      }, 0);
    },
    [sidebarOpen, navigate],
  );

  const refreshUserProfile = useCallback(async () => {
    const user = session.data?.user;
    if (!user?.email) return;
    const userId = user.id || user.email;
    const res = await apiGet<{
      name?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      profilePhoto?: string | null;
    }>(`/api/users/${encodeURIComponent(userId)}`);
    if (res.ok && res.data) {
      setUserProfile({
        name: res.data.name,
        firstName: res.data.firstName,
        lastName: res.data.lastName,
        profilePhoto: res.data.profilePhoto,
      });
    }
    session.refresh();
  }, [session]);

  const topNavLinkClass = (active: boolean) =>
    cn(
      "inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold whitespace-nowrap",
      active
        ? "bg-gray-100 text-indigo-600 dark:bg-white/10 dark:text-white"
        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10",
    );

  useEffect(() => {
    if (session.status !== "unauthenticated") return;
    window.location.replace(authSignInUrl(pathname));
  }, [session.status, pathname]);

  if (session.status === "loading" || session.status === "unauthenticated") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-zinc-950">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-indigo-600" />
          <p className="text-gray-600 dark:text-gray-400">
            {session.status === "unauthenticated" ? "Redirecting to sign in…" : "Loading…"}
          </p>
        </div>
      </div>
    );
  }

  const railCommon: Omit<SidebarRailProps, "logoFrameClass" | "onNavigate" | "mobileNavClick"> = {
    sidebarLogoSrc,
    sidebarLogoClass,
    themeDefaultSidebarLogo: !tenantLogoUrl,
    sidebarTitle,
    sidebarItems,
    showCustomAppsCatalog,
    sidebarFooterHref,
    sidebarFooterLabel,
    sidebarFooterActive,
    appSettingsContext,
    pathname,
    sidebarGroupOverride,
    toggleSidebarGroup,
    isGroupExpanded,
    isActiveHref,
    sidebarFooterLine,
  };

  const accountManagementHref =
    userRole === "super_admin" || userRole === "account_owner" ? "/organization" : null;

  return (
    <div
      className="min-h-screen dark:bg-zinc-950"
      style={{ ["--one-rail-w" as string]: `${sidebarWidth}px` }}
    >
      {sidebarOpen ? (
        <div className="fixed inset-0 z-50 xl:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-gray-900/80 transition-opacity"
            aria-label="Close sidebar backdrop"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative flex h-full max-w-xs">
            <div className="w-full max-w-xs bg-[#111829]">
              <SidebarRail
                {...railCommon}
                logoFrameClass="h-24"
                onNavigate={() => setSidebarOpen(false)}
                mobileNavClick={handleMobileSidebarNavClick}
              />
            </div>
            <button
              type="button"
              className="absolute left-full top-4 ml-2 rounded-full bg-slate-800 p-2 text-white"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="relative hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:flex-col"
        style={{ width: sidebarWidth }}
      >
        <SidebarRail
          {...railCommon}
          logoFrameClass="h-16"
          collapsed={sidebarIconOnly}
          showSidebarCollapseToggle
          onToggleSidebarCollapsed={toggleSidebarRetractExpand}
        />
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          className="absolute inset-y-0 -right-1 z-20 w-2 cursor-col-resize touch-none select-none"
          onPointerDown={handleSidebarResizePointerDown}
        />
      </div>

      <div className="bg-white pb-10 dark:bg-zinc-950 xl:pl-[var(--one-rail-w)]">
        <div className="shadow-xs sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 dark:border-white/5 dark:bg-gray-900 dark:shadow-none sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-gray-900 dark:text-white xl:hidden"
            aria-label="Open sidebar"
          >
            <Menu className="size-5" />
          </button>

          <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-visible">
            {pinnedTopNavApps.map((app) => {
              const active = topNavItemActive(pathname, app.slug, app.topNavHref);
              const markId = atwMarkIdForAppSlug(app.slug);
              return (
                <Link key={app.slug} to={app.topNavHref} className={topNavLinkClass(active)}>
                  {markId ? (
                    <AtwProductMark
                      id={markId}
                      size={16}
                      className="dark:brightness-0 dark:invert"
                    />
                  ) : null}
                  {app.label}
                </Link>
              );
            })}
            {overflowTopNavApps.length > 0 ? (
              <div ref={topNavOverflowRef} className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setTopNavOverflowOpen((open) => !open)}
                  className={topNavLinkClass(overflowActive)}
                  aria-label="Show more apps"
                  aria-haspopup="menu"
                  aria-expanded={topNavOverflowOpen}
                >
                  <ChevronDown className="size-4" />
                </button>
                {topNavOverflowOpen ? (
                  <div
                    role="menu"
                    className="absolute left-0 top-full z-50 mt-2 w-52 origin-top-left rounded-md border border-gray-200 bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-white/10 dark:bg-gray-900 dark:ring-white/10"
                  >
                    {overflowTopNavApps.map((app) => {
                      const active = topNavItemActive(pathname, app.slug, app.topNavHref);
                      const markId = atwMarkIdForAppSlug(app.slug);
                      return (
                        <Link
                          key={app.slug}
                          to={app.topNavHref}
                          role="menuitem"
                          className={cn(
                            "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold",
                            active
                              ? "bg-gray-100 text-indigo-600 dark:bg-white/10 dark:text-white"
                              : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10",
                          )}
                          onClick={() => setTopNavOverflowOpen(false)}
                        >
                          {markId ? (
                            <AtwProductMark
                              id={markId}
                              size={16}
                              className="dark:brightness-0 dark:invert"
                            />
                          ) : null}
                          {app.label}
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </nav>

          <form
            className="hidden md:block"
            onSubmit={(e) => {
              const fd = new FormData(e.currentTarget);
              const q = String(fd.get("q") || "").trim();
              navigate(q ? `/crm/search?q=${encodeURIComponent(q)}` : "/crm/search");
            }}
          >
            <label className="relative block w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="search"
                name="q"
                placeholder="Search CRM"
                className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none ring-indigo-500 placeholder:text-gray-400 focus:ring-2 dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </label>
          </form>

          <div className="flex items-center gap-2">
            <TrialStatusBadge />
            <button
              type="button"
              onClick={() => setOrgSheetOpen(true)}
              className="rounded-full p-0.5 text-gray-600 ring-2 ring-transparent hover:ring-indigo-200 dark:text-gray-300 dark:hover:ring-white/20"
              aria-label="Organization and account menu"
            >
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="size-9 rounded-full object-cover"
                />
              ) : (
                <span className="flex size-9 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
                  {displayName
                    .split(" ")
                    .filter(Boolean)
                    .map((n) => n[0]!)
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)}
                </span>
              )}
            </button>
          </div>
        </div>

        <main className="flex min-h-0 flex-1 flex-col bg-slate-100 p-3 sm:p-5 dark:bg-zinc-950">
          <TrialExpiredNotice />
          <div className="mx-auto min-h-full w-full max-w-[1920px] flex-1 overflow-x-auto rounded-xl border border-slate-200/90 bg-white shadow-sm dark:border-white/10 dark:bg-zinc-900">
            <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-6">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      <DashboardUtilityBar />

      <DashboardOrgSwitcherSheet
        open={orgSheetOpen}
        onOpenChange={setOrgSheetOpen}
        pathname={pathname}
        sessionOrgId={sessionOrgId}
        sessionAccountDomain={sessionAccountDomain}
        userLabel={displayName}
        userImageSrc={avatarSrc}
        accountManagementHref={accountManagementHref}
        onOpenProfile={() => setProfileDialogOpen(true)}
      />

      <ProfileSettingsDialog
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        onProfileUpdate={() => void refreshUserProfile()}
      />
    </div>
  );
}
